import { AbilityBuilder, createMongoAbility, type MongoAbility } from '@casl/ability';
import { get, isEmpty } from 'lodash';

import { Permission, Action } from '../../shared/types';
import { PERMISSION } from './permission';


export type AppAbility = MongoAbility<[Action, string]>;

/**
 * Defines the user's ability (permissions) based on their platform and role using CASL.
 *
 * This function constructs an instance of the CASL `Ability` class by reading permissions
 * from a global `PERMISSION` map based on the provided `permission` object's `platform`
 * and `role` properties.
 *
 * Permissions are categorized into "router" (for route/view access) and "features"
 * (for specific UI/API capabilities).
 *
 * @param {Permission} permission - The object containing the user's platform and role.
 * Expected shape: `{ platform: string, role: string, ... }`.
 * @returns {Ability} A CASL `Ability` instance containing the defined rules.
 *
 * @example
 * Assuming Permission is { platform: 'web', role: 'admin' }
 * const userAbility = defineAbilityFor({ platform: 'web', role: 'admin' });
 * if (userAbility.can('read', 'Dashboard')) {
 * User can access the Dashboard
 * }
 */
export function defineAbilityFor(permission: Permission): AppAbility {
  const { can, build } = new AbilityBuilder<AppAbility>(createMongoAbility);
  const { platform, role } = permission;

  // --- 1. Fetch Platform Permissions ---
  const platformPermissions = get(PERMISSION, platform);

  if (!platformPermissions) {
    console.warn(`No permissions defined for platform: ${platform}`);
    // Return an empty ability if the platform is not found
    return build();
  }

  // --- 2. Fetch Role Permissions ---
  const rolePermissions = get(platformPermissions, role);

  if (!rolePermissions) {
    console.warn(`No permissions defined for role: ${role} in platform: ${platform}`);
    // Return an empty ability if the role is not found for the platform
    return build();
  }

  // --- 3. Process Router Permissions (Route Access) ---
  const routers = get(rolePermissions, "router", []);
  if (!isEmpty(routers)) {
    routers.forEach(({ subject, action }: { subject: string; action: Action }) => {
      // Defines an ability rule: The user can perform the 'action' on the 'subject' (e.g., can('read', 'UsersPage'))
      can(action, subject);
    });
  }

  // --- 4. Process Feature Permissions (Specific Capabilities) ---
  const features = get(rolePermissions, "features", []);
  if (!isEmpty(features)) {
    features.forEach(({ subject, action }: { subject: string; action: Action }) => {
      // Defines an ability rule: The user can perform the 'action' on the 'subject' (e.g., can('update', 'UserProfile'))
      can(action, subject);
    });
  }

  // --- 5. Return the Ability Instance ---
  return build();
}
